import { useContext, useState } from "react";

import styles from "./styles/PosSummary.module.css";
import DataContext from "../../../context/DataContext";
import {
    formatDateToLocalDate,
    formatTimeToLocalTime,
} from "../../../utils/utils";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../context/AuthContext";
import useNumberFormat from "../../../hooks/useNumberFormat";

/**
 * Transaction info component
 * Return the content of the info window in the summary modal
 */
const PosSummaryDetails = ({
    currentTransaction, // {Object} - The object data for the transaction to be deleted
}) => {
    const { t } = useTranslation();
    const formatNum = useNumberFormat();
    const {
        connection, // {Object} - The client's settings
    } = useContext(DataContext);

    const {
        user, // {Object} - The authenticated user's data
    } = useContext(AuthContext);

    const currency = useCurrencySymbol(true);

    const transactions = currentTransaction?.transactions;

    /**
     * The sum of a selected type (amount ot tax)
     * @param {Array} charges - The charges for each transaction,
     * each transaction (the main transaction the one to be canceled) can contain multiple transactions (sub transactions)
     * inside of it (if multiple tickets or services were paid).
     * And each sub transaction there are charges, these charges show how much and what was paid for that sub transaction.
     * example:
     * we have paid for a ticket and a service, so the main transaction will contain 2 sub transactions
     * 1 for the ticket and the other for the service.
     * If we lokk at the ticket sub transaction, it my contain one or multiple charges, an example of charge can be a penalty or base amount.
     * The sum of these charges is the amount of money paid
     * These charges also contain tax amount
     *
     * @param {String} sumType - The type of sum (can be amount or tax)
     * @returns {Float} - The sum of the selected type
     */
    const getAmountTax = (charges, sumType) => {
        let total = 0;
        charges &&
            charges.length &&
            charges.forEach((item) => {
                if (sumType === "amount") {
                    total += item?.amount ? item?.amount : 0;
                } else if (sumType === "tax") {
                    total += item?.taxAmount ? item?.taxAmount : 0;
                } else {
                    total += 0;
                }
            });

        return total;
        // return Number(total || 0).toFixed(2);
    };

    /**
     * The sum of the selected type for a section (sum type can be amount or tax and type can be violation or service)
     * @param {String} type - The type of the section to get the sum from (violation, service)
     * @param {String} sumType  - The type of the sum (amount, tax)
     * @returns {Float} -the sum
     */
    const getSumAllSection = (type, sumType) => {
        let total = 0;

        transactions &&
            transactions.length &&
            transactions
                .filter((item) => item?.type === type)
                .forEach((item) => {
                    total += parseFloat(getAmountTax(item?.charges, sumType));
                });

        return total;
        // return Number(total || 0).toFixed(2);
    };

    /**
     * The sum of tax for all the items in the main transaction (tickets, services).
     * Used to show the tax in the payments section.
     * @returns {Float} - the sam of all the tax in the main transaction
     */
    const getSumAllTax = () => {
        let total = 0;

        transactions &&
            transactions.length &&
            transactions.forEach((item) => {
                item?.charges.forEach((subItem) => {
                    total += item?.taxAmount ? item?.taxAmount : 0;
                });
            });

        return total;
        // return Number(total || 0).toFixed(2);
    };

    /**
     * The ticket row component
     * The row of a ticket in the info window
     * @returns {JSSX-Element} = The row for the ticket
     */
    const TicketRow = ({ ticketNumber, amount, tax }) => {
        return (
            <div className={`${styles.modal_details_row} flex-container`}>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.name_tag}`}
                >
                    {ticketNumber}
                </div>

                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_currency}  ${styles.hide_tickets}  ${styles.tax_tag}`}
                >
                    {currency}
                    {formatNum(tax)}
                </div>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_currency} ${styles.amount_tag}`}
                >
                    {currency}
                    {formatNum(amount)}
                </div>
            </div>
        );
    };

    /**
     * The service row component
     * The row of a service in the info window
     * @returns {JSSX-Element} = The row for the service
     */
    const ServiceRow = ({ serviceName, code, amount, tax }) => {
        return (
            <div className={`${styles.modal_details_row} flex-container`}>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.name_tag}`}
                >
                    {t(serviceName)}
                </div>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.code_tag} ${styles.hide_services_2nd}`}
                >
                    {code}
                </div>

                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_currency}  ${styles.tax_tag} ${styles.hide_services}`}
                >
                    {currency}
                    {formatNum(tax)}
                </div>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_currency} ${styles.amount_tag}`}
                >
                    {currency}
                    {formatNum(amount)}
                </div>
            </div>
        );
    };
    /**
     * The instalment component
     * The row of a service in the info window
     * @returns {JSSX-Element} = The row for the service
     */
    const InstalmentRow = ({ instalmentNumber, amount, tax }) => {
        return (
            <div className={`${styles.modal_details_row} flex-container`}>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_text} ${styles.name_tag}`}
                >
                    {instalmentNumber}
                </div>

                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_currency}  ${styles.hide_instalments} ${styles.tax_tag}`}
                >
                    {currency}
                    {formatNum(tax)}
                </div>
                <div
                    className={`${styles.modal_details_row_entry} ${styles.modal_details_row_text} ${styles.amount_tag}`}
                >
                    {currency}
                    {formatNum(amount)}
                </div>
            </div>
        );
    };

    const Settlement = ({ settlement }) => {
        return (
            <div className={`${styles.settlement_container} v-flex-container`}>
                <div
                    className={`${styles.settlement_header_container} flex-container`}
                >
                    <div>
                        <span
                            className={` ${styles.modal_details_header_entry} ${styles.settlement_header_label}`}
                        >
                            {`${t(`settlement`)}:`}
                        </span>
                        <span className={`${styles.settlement_header_entry}`}>
                            {settlement?.code}
                        </span>
                    </div>
                </div>
                <div
                    className={`${styles.modal_details_instalments_header} flex-container`}
                >
                    <span
                        className={`${styles.modal_details_header_entry} ${styles.name_tag}`}
                    >
                        {t(`instalment`)}
                    </span>

                    <span
                        className={`${styles.modal_details_header_entry} ${styles.hide_instalments} ${styles.tax_tag}`}
                    >
                        {t(`tax`)}
                    </span>
                    <span
                        className={`${styles.modal_details_header_entry}   ${styles.amount_tag}`}
                    >
                        {t(`amount`)}
                    </span>
                </div>

                {settlement?.charges?.length &&
                    settlement?.charges.map((charge) => (
                        <InstalmentRow
                            key={charge?.description}
                            instalmentNumber={charge?.description.split("#")[1]}
                            amount={charge?.totalAmount}
                            tax={charge?.taxAmount}
                        />
                    ))}
                <div
                    className={`${styles.settlement_footer_container} flex-container`}
                >
                    <div>
                        <span
                            className={` ${styles.modal_details_header_entry}  ${styles.settlement_header_label}`}
                        >
                            {`${t(`settlement total`)}:`}
                        </span>
                        <span className={` ${styles.settlement_header_entry}`}>
                            {currency}
                            {formatNum(
                                getAmountTax(settlement?.charges, "amount"),
                            )}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={`${styles.modal_details_main_container} v-flex-container`}
        >
            <div
                className={`${styles.modal_details_header_info_container} flex-container`}
            >
                <div
                    className={`${styles.modal_details_header_sub_container}  v-flex-container`}
                >
                    <div
                        className={`${styles.modal_details_header_entry_container}`}
                    >
                        <span
                            className={` ${styles.details_session_header_label}`}
                        >{`${t("transaction")} : `}</span>
                        <span
                            className={` ${styles.details_session_header_entry}`}
                        >
                            {currentTransaction?.referenceNo}
                        </span>
                    </div>

                    <div
                        className={`${styles.modal_details_header_entry_container}`}
                    >
                        <span
                            className={` ${styles.details_session_header_label}`}
                        >{`${t("operator")} : `}</span>
                        <span
                            className={` ${styles.details_session_header_entry}`}
                        >
                            {user?.cashierID}
                        </span>
                    </div>
                </div>
                <div
                    className={` ${styles.modal_details_header_sub_container}  ${styles.modal_details_header_date_container}   flex-container`}
                >
                    <div
                        className={` ${styles.modal_details_header_right_container} v-flex-container`}
                    >
                        <div
                            className={`${styles.modal_details_header_entry_container} `}
                        >
                            <span
                                className={` ${styles.details_session_header_label}`}
                            >{`${t("date")} : `}</span>
                            <span
                                className={` ${styles.details_session_header_entry}`}
                            >
                                {formatDateToLocalDate(
                                    currentTransaction?.paymentDateTime,
                                )}
                            </span>
                        </div>
                        <div
                            className={`${styles.modal_details_header_entry_container}`}
                        >
                            <span
                                className={` ${styles.details_session_header_label}`}
                            >{`${t("time")} : `}</span>
                            <span
                                className={` ${styles.details_session_header_entry}`}
                            >
                                {formatTimeToLocalTime(
                                    currentTransaction?.paymentDateTime,
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {/* --------------------------------------------------------------------------------Transaction Details    */}
            <div
                className={` ${styles.modal_details_main_items_container} flex-container`}
            >
                <div
                    className={` ${styles.modal_details_items_container} v-flex-container`}
                >
                    {transactions?.length &&
                    transactions?.filter((item) => item?.type === "violation")
                        .length ? (
                        //_____________________________________________________________________________________Tickets

                        <>
                            <div
                                className={`${styles.modal_details_items_header_container} flex-container`}
                            >
                                <h3>{t(`tickets`)}</h3>
                            </div>
                            <div
                                className={`${styles.modal_details_tickets_header} flex-container`}
                            >
                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.name_tag} `}
                                >
                                    {t("ticket")}
                                </span>

                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.hide_tickets} ${styles.tax_tag}`}
                                >
                                    {t("tax")}
                                </span>
                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.amount_tag}`}
                                >
                                    {t("amount")}
                                </span>
                            </div>
                            {/* Tickets rows */}
                            {transactions
                                .filter((item) => item?.type === "violation")
                                .map((item) => (
                                    <TicketRow
                                        key={item?.transactionID}
                                        ticketNumber={item?.code}
                                        amount={getAmountTax(
                                            item?.charges,
                                            "amount",
                                        )}
                                        tax={getAmountTax(item?.charges, "tax")}
                                    />
                                ))}
                            {/* The sum for the tickets section */}
                            <div
                                className={`${styles.main_sum_container} flex-container`}
                            >
                                <div
                                    className={`${styles.sum_container} flex-container`}
                                >
                                    <span className={`${styles.sum_label}`}>
                                        {`${t("tax sum")} :`}
                                    </span>
                                    <span className={`${styles.sum_value} `}>
                                        {currency}
                                        {formatNum(
                                            getSumAllSection(
                                                "violation",
                                                "tax",
                                            ),
                                        )}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.sum_container} flex-container`}
                                >
                                    <span className={`${styles.sum_label}`}>
                                        {`${t("amount sum")} :`}
                                    </span>
                                    <span className={`${styles.sum_value} `}>
                                        {currency}
                                        {formatNum(
                                            getSumAllSection(
                                                "violation",
                                                "amount",
                                            ),
                                        )}
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                    {transactions &&
                    transactions.length &&
                    transactions.filter((item) => item?.type === "case")
                        .length ? (
                        //_____________________________________________________________________________________Settlements

                        <>
                            <div
                                className={`${styles.modal_details_items_header_container} flex-container`}
                            >
                                <h3>{t(`settlements`)}</h3>
                            </div>
                            {/* settlement*/}
                            <div
                                className={`${styles.main_settlements_container} v-flex-container`}
                            >
                                {transactions
                                    .filter((item) => item?.type === "case")
                                    .map((item) => (
                                        <Settlement
                                            settlement={item}
                                            key={item?.transactionID}
                                        />
                                    ))}
                                {/* The sum for the tickets section */}
                            </div>
                            <div
                                className={`${styles.main_sum_container} flex-container`}
                            >
                                <div
                                    className={`${styles.sum_container} flex-container`}
                                >
                                    <span className={`${styles.sum_label}`}>
                                        {`${t("tax sum")} :`}
                                    </span>
                                    <span className={`${styles.sum_value} `}>
                                        {currency}
                                        {formatNum(
                                            getSumAllSection("case", "tax"),
                                        )}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.sum_container} flex-container`}
                                >
                                    <span className={`${styles.sum_label}`}>
                                        {`${t("amount sum")} :`}
                                    </span>
                                    <span className={`${styles.sum_value} `}>
                                        {currency}
                                        {formatNum(
                                            getSumAllSection("case", "amount"),
                                        )}
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}

                    {transactions &&
                    transactions.length &&
                    transactions.filter((item) => item?.type === "service")
                        .length ? (
                        //_____________________________________________________________________________________Services

                        <>
                            <div
                                className={`${styles.modal_details_items_header_container} flex-container`}
                            >
                                <h3>{t(`services`)}</h3>
                            </div>
                            <div
                                className={`${styles.modal_details_services_header} flex-container`}
                            >
                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.name_tag}`}
                                >
                                    {t(`service`)}
                                </span>
                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.code_tag} ${styles.hide_services_2nd}`}
                                >
                                    {t(`code`)}
                                </span>
                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.tax_tag} ${styles.hide_services}`}
                                >
                                    {t(`tax`)}
                                </span>
                                <span
                                    className={`${styles.modal_details_header_entry} ${styles.amount_tag}`}
                                >
                                    {t(`amount`)}
                                </span>
                            </div>
                            {/* Services rows */}
                            {transactions
                                .filter((item) => item?.type === "service")
                                .map((item) => (
                                    <ServiceRow
                                        key={item?.transactionID}
                                        serviceName={
                                            item?.charges[0]?.description
                                        }
                                        code={item?.code}
                                        amount={getAmountTax(
                                            item?.charges,
                                            "amount",
                                        )}
                                        tax={getAmountTax(item?.charges, "tax")}
                                    />
                                ))}
                            {/* Thw sum for the services sections */}
                            <div
                                className={`${styles.main_sum_container} flex-container`}
                            >
                                <div
                                    className={`${styles.sum_container}  flex-container`}
                                >
                                    <span
                                        className={`${styles.sum_label} ${styles.sum_tax} `}
                                    >
                                        {`${t("tax sum")} :`}
                                    </span>
                                    <span className={`${styles.sum_value} `}>
                                        {currency}
                                        {formatNum(
                                            getSumAllSection("service", "tax"),
                                        )}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.sum_container}  flex-container`}
                                >
                                    <span
                                        className={`${styles.sum_label} ${styles.sum_amount}`}
                                    >
                                        {`${t("amount sum")} :`}
                                    </span>
                                    <span className={`${styles.sum_value} `}>
                                        {currency}
                                        {formatNum(
                                            getSumAllSection(
                                                "service",
                                                "amount",
                                            ),
                                        )}
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                </div>

                {/* --------------------------------------------------------------------------------Transaction Details    */}
                <div
                    className={`${styles.modal_details_divider_container} flex-container`}
                >
                    <div
                        className={`${styles.modal_details_divider} flex-container`}
                    ></div>
                </div>
                <div
                    className={`${styles.modal_details_main_payment_container} v-flex-container`}
                >
                    <div
                        className={`${styles.modal_details_items_header_container} flex-container`}
                    >
                        <h3>{t("payments")}</h3>
                    </div>

                    <div
                        className={`${styles.details_payment_type} v-flex-container`}
                    >
                        {/* The payments methods */}
                        {currentTransaction?.paymentDetails &&
                            currentTransaction?.paymentDetails.length &&
                            currentTransaction?.paymentDetails.map((item) => (
                                <div
                                    key={item?.paymentMethod}
                                    className={`${styles.details_payment_type_entry_container} flex-container`}
                                >
                                    <span
                                        className={`${styles.details_payment_type_label}`}
                                    >
                                        {`${t(item?.paymentMethod)} : `}
                                    </span>
                                    <span
                                        className={`${styles.details_payment_type_entry}`}
                                    >
                                        {`   ${currency}${formatNum(
                                            item?.amount,
                                        )}`}
                                    </span>
                                </div>
                            ))}
                    </div>
                    {/* Sum of all tax and amounts paid */}
                    <div
                        className={`${styles.details_payment_sum} v-flex-container`}
                    >
                        <div
                            className={`${styles.details_payment_sum_entry_container} flex-container `}
                        >
                            <span
                                className={`${styles.details_payment_sum_label}`}
                            >
                                {`${t("tax")} : `}
                            </span>
                            <span
                                className={`${styles.details_payment_sum_entry}`}
                            >
                                {`   ${currency}${formatNum(getSumAllTax())}`}
                            </span>
                        </div>

                        <div
                            className={`${styles.details_payment_sum_entry_container} flex-container `}
                        >
                            <span
                                className={`${styles.details_payment_sum_label}`}
                            >
                                {`${t("total")} : `}
                            </span>
                            <span
                                className={`${styles.details_payment_sum_entry}`}
                            >
                                {`   ${currency}${formatNum(
                                    currentTransaction?.totalAmount,
                                )}`}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PosSummaryDetails;
